// Inline per-column table filters, shared by every report.
// A filter is { op: 'contains'|'eq'|'lte'|'gte'|'between'|'notBetween', v1, v2 } applied to the
// column's DISPLAYED text, so what you filter on is exactly what you can see in the cell.
//
// The screens differ in how they produce that text (render(item) vs text(row) vs dec(row[k], d)),
// so matchFilterText takes the finished string and each screen passes its own. ColumnFilterRow
// renders the controls; a screen only supplies its column list and the text for each cell.

// Operators, by what the column actually holds. A column of names has no use for "≤" and a column
// of centimetres has none for "starts with"; offering every operator everywhere just means most of
// the list is noise wherever you open it.
//
// FILTER_OPS is the numeric set and keeps its name: the reports that predate this import it
// directly, and every one of them filters figures.
const FILTER_OPS = [
    { value: 'contains', label: 'contains' },
    { value: 'eq', label: '=' },
    { value: 'lte', label: '≤' },
    { value: 'gte', label: '≥' },
    { value: 'between', label: 'between' },
    { value: 'notBetween', label: 'not between' },
];

const TEXT_OPS = [
    { value: 'contains', label: 'contains' },
    // "=" reads as arithmetic; on a column of names the same operator is "equals to".
    { value: 'eq', label: 'equals to' },
    { value: 'startsWith', label: 'starts with' },
    { value: 'endsWith', label: 'ends with' },
];

// A status column is a choice, not an expression, so it gets a single picker rather than an
// operator and a box. '' is All, and reads as "no filter" to isFilterActive below.
const STATUS_OPS = [
    { value: '', label: 'All' },
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
];

const opsForType = (type) => (type === 'text' ? TEXT_OPS : FILTER_OPS);

// Ops that take a second bound, so the screens don't have to name them one by one.
const isRangeOp = (op) => op === 'between' || op === 'notBetween';

const BLANK_FILTER = { op: 'contains', v1: '', v2: '' };

// Empty v1 means inactive, except for the range ops where either bound on its own is enough.
const isFilterActive = (f) => f && (((f.v1 ?? '') !== '') || (isRangeOp(f.op) && (f.v2 ?? '') !== ''));

const matchFilterText = (raw, f) => {
    if (!isFilterActive(f)) return true;
    const text = (raw == null ? '' : String(raw)).trim();
    const cellNum = parseFloat(text);
    const n1 = parseFloat(f.v1);
    if (f.op === 'contains') return text.toLowerCase().includes(String(f.v1).toLowerCase());
    if (f.op === 'startsWith') return text.toLowerCase().startsWith(String(f.v1).toLowerCase());
    if (f.op === 'endsWith') return text.toLowerCase().endsWith(String(f.v1).toLowerCase());
    // Status is an exact match on purpose. "contains" would be wrong here in a way that looks
    // right: "Inactive" contains "Active", so picking Active would surface the disabled rows too.
    if (f.op === 'status') return text.toLowerCase() === String(f.v1).toLowerCase();
    // "=" compares numerically when both sides are numbers ("2" matches 2.00), else exact text.
    if (f.op === 'eq') return !isNaN(n1) && !isNaN(cellNum) ? cellNum === n1 : text.toLowerCase() === String(f.v1).toLowerCase();
    if (f.op === 'lte') return !isNaN(cellNum) && !isNaN(n1) && cellNum <= n1;
    if (f.op === 'gte') return !isNaN(cellNum) && !isNaN(n1) && cellNum >= n1;
    if (isRangeOp(f.op)) {
        const lo = (f.v1 ?? '') === '' ? -Infinity : n1;
        const hi = (f.v2 ?? '') === '' ? Infinity : parseFloat(f.v2);
        // A blank or non-numeric cell matches neither "between" nor "not between" -- same rule the
        // other numeric ops follow, so a range filter never surfaces the rows that have no figure.
        if (isNaN(cellNum)) return false;
        const inside = cellNum >= lo && cellNum <= hi;
        return f.op === 'between' ? inside : !inside;
    }
    return true;
};

// Merge a partial change into one column's filter, keeping the other fields.
const patchFilter = (filters, key, patch) => ({ ...filters, [key]: { ...BLANK_FILTER, ...filters[key], ...patch } });

export { FILTER_OPS, TEXT_OPS, STATUS_OPS, opsForType, BLANK_FILTER, isRangeOp, isFilterActive, matchFilterText, patchFilter };
